import { useState, useEffect } from 'react';
import { dashboardAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Factory, Package, Truck, ShieldCheck, TrendingDown, TrendingUp, Clock, Plus, Leaf } from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area } from 'recharts';
import QuickPCFReport from '@/components/QuickPCFReport';

const COLORS = ['#166534', '#4F46E5', '#DC2626', '#D97706', '#0891B2', '#7C3AED'];

function StatCard({ icon: Icon, label, value, suffix, sparkline, trend, delay }) {
  return (
    <div className={`cp-stat-card animate-fade-in stagger-${delay}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-heading font-bold tracking-tight">
            {typeof value === 'number' ? value.toLocaleString() : value}
            {suffix && <span className="text-sm font-normal text-muted-foreground ml-1">{suffix}</span>}
          </p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
      </div>
      {sparkline && sparkline.length > 0 && (
        <div className="mt-3 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkline}>
              <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.1} strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {trend !== undefined && (
        <div className={`mt-2 flex items-center gap-1 text-xs font-medium ${trend < 0 ? 'text-green-600' : 'text-red-500'}`}>
          {trend < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
          {Math.abs(trend)}% vs last month
        </div>
      )}
    </div>
  );
}

function ProgressCircle({ value, size = 80 }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 95 ? '#166534' : value >= 90 ? '#D97706' : '#DC2626';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="cp-progress-circle">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
      </svg>
      <span className="absolute text-sm font-heading font-bold">{value}%</span>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickReportOpen, setQuickReportOpen] = useState(false);

  useEffect(() => {
    dashboardAPI.getStats().then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <DashboardSkeleton />;
  if (!data) return <p className="text-muted-foreground">Failed to load dashboard data.</p>;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="cp-page-title">Dashboard</h1>
        <Button onClick={() => setQuickReportOpen(true)} className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20" data-testid="new-pcf-report-btn">
          <Plus className="w-4 h-4 mr-2" /> New PCF Report
        </Button>
      </div>

      <QuickPCFReport open={quickReportOpen} onClose={() => setQuickReportOpen(false)} />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Factory} label="Total Emissions" value={data.total_emissions} suffix="tCO2" sparkline={data.sparkline} trend={-4.2} delay={1} />
        <StatCard icon={Package} label="Total Products" value={data.total_products} delay={2} />
        <StatCard icon={Truck} label="Active Suppliers" value={data.active_suppliers} delay={3} />
        <div className={`cp-stat-card animate-fade-in stagger-4`} data-testid="stat-cbam-readiness">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">CBAM Readiness</p>
              <p className="text-2xl font-heading font-bold tracking-tight">{data.cbam_readiness}%</p>
            </div>
            <ProgressCircle value={data.cbam_readiness} size={56} />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cp-chart-card animate-fade-in" data-testid="emissions-trend-chart">
          <h3 className="text-sm font-heading font-semibold mb-4">Monthly Emission Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.emissions_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="emissions" stroke="#166534" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cp-chart-card animate-fade-in" data-testid="emissions-sector-chart">
          <h3 className="text-sm font-heading font-semibold mb-4">Emissions by Sector</h3>
          <div className="h-64 flex items-center justify-center">
            {data.emissions_by_sector.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.emissions_by_sector} dataKey="emissions" nameKey="sector" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2} strokeWidth={0}>
                    {data.emissions_by_sector.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground">No sector data available</p>
            )}
            {data.emissions_by_sector.length > 0 && (
              <div className="space-y-2 ml-2">
                {data.emissions_by_sector.map((item, i) => (
                  <div key={item.sector} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-muted-foreground">{item.sector}</span>
                    <span className="font-mono font-semibold ml-auto">{item.emissions.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="cp-chart-card animate-fade-in" data-testid="recent-activity">
        <h3 className="text-sm font-heading font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" /> Recent Activity
        </h3>
        <div className="cp-table-wrapper">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead className="text-right">Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recent_activity.map((act) => (
                <TableRow key={act.id}>
                  <TableCell className="text-sm">{act.action}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{new Date(act.timestamp).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {data.recent_activity.length === 0 && (
                <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-8">No recent activity</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-40 bg-muted rounded animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1, 2].map(i => <div key={i} className="h-72 bg-muted rounded-xl animate-pulse" />)}
      </div>
    </div>
  );
}
